import { Link, useBlocker } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import {
  type Articolo as DatiArticolo,
  useAnteprimaGs1,
  useArticolo,
  useDefinizioneFiltriArticoli,
  useGeneraGs1,
  useSalvaArticolo,
} from '../api/articoli.js';
import { ErroreApi } from '../api/cliente.js';
import { CampoBc } from '../componenti/CampoBc.js';
import { Dato } from '../componenti/Dato.js';
import { data, quantita } from '../formati.js';
import { SKU_ASSENTE, nomeArticolo } from './Articoli.js';
import { t } from '../i18n/lingua.js';
import { usePuoAprire } from '../permessi.js';

/**
 * La scheda di un articolo.
 *
 * Per anni ha detto «si modifica in Business Central», e per metà non era
 * vero: BC manda numero, SKU, descrizione e unità di misura, mentre
 * **attributi, codici GS1, case pack, formati, pesi e imballo sono dati del
 * CRM** — BC non li ha e non li avrà mai. Erano visibili e inservibili.
 *
 * Il salvataggio è esplicito come sulle anagrafiche: c'è un pulsante Salva e
 * finché non lo premi non è successo niente.
 *
 * Sui campi che BC manda davvero la scheda avverte: il CRM gli articoli a BC
 * non li manda, e la prossima importazione li riporta com'erano di là.
 */

/** I sei codici GS1, con la sigla che si usa parlandone. */
const CODICI = [
  { chiave: 'codeG12', etichetta: t('GTIN-12') },
  { chiave: 'codeG13', etichetta: t('GTIN-13') },
  { chiave: 'codeG14A', etichetta: t('GTIN-14 A') },
  { chiave: 'codeG14B', etichetta: t('GTIN-14 B') },
  { chiave: 'codeG14APackage', etichetta: t('GTIN-14 A imballo') },
  { chiave: 'codeG14BPackage', etichetta: t('GTIN-14 B imballo') },
] as const;

/** Formati e pesi: dati del CRM, si scrivono. */
const FORMATI = [
  { chiave: 'grossFormatGr', etichetta: t('Formato lordo (gr)') },
  { chiave: 'packFormatGr', etichetta: t('Formato confezione (gr)') },
  { chiave: 'packFormatOz', etichetta: t('Formato confezione (oz)') },
  { chiave: 'packsPerPallet', etichetta: t('Confezioni per pallet') },
  { chiave: 'platformsPerContainer', etichetta: t('Pedane per container') },
] as const;

/**
 * I nove campi di imballo.
 *
 * Nel legacy erano nove anagrafiche separate con una riga ciascuna e nessun
 * articolo le referenziava: nei dati travasati sono tutti vuoti. Adesso hanno
 * un posto in cui scriverli, che è quello che mancava perché si riempissero.
 */
// i limiti sono quelli del servizio: le caselle ne accettavano 100 per tutti,
// e oltre 50 il servizio rifiutava con un messaggio che non diceva quale
const IMBALLO = [
  { chiave: 'casePackDimensions', etichetta: t('Dimensioni cartone'), limite: 100 },
  { chiave: 'casePackGrossWeight', etichetta: t('Peso lordo cartone'), limite: 50 },
  { chiave: 'casePackCubed', etichetta: t('Volume cartone'), limite: 50 },
  { chiave: 'casePacksPerPallet', etichetta: t('Cartoni per pallet'), limite: 50 },
  { chiave: 'palletConfiguration', etichetta: t('Configurazione pallet'), limite: 100 },
  { chiave: 'palletGrossWeight', etichetta: t('Peso lordo pallet'), limite: 50 },
  { chiave: 'unitDimensions', etichetta: t('Dimensioni unità'), limite: 100 },
  { chiave: 'unitSize', etichetta: t('Misura unità'), limite: 50 },
  { chiave: 'unitWeight', etichetta: t('Peso unità'), limite: 50 },
] as const;

/** I sei attributi più case pack e unità: tendine, non caselle di testo. */
const TENDINE = [
  { campo: 'lineId', etichetta: t('Linea'), opzioni: 'lines' },
  { campo: 'productId', etichetta: t('Prodotto'), opzioni: 'products' },
  { campo: 'typologyId', etichetta: t('Tipologia'), opzioni: 'typologies' },
  { campo: 'packagingId', etichetta: t('Confezionamento'), opzioni: 'packagings' },
  { campo: 'formatId', etichetta: t('Formato'), opzioni: 'formats' },
  { campo: 'variantId', etichetta: t('Variante'), opzioni: 'variants' },
  { campo: 'casePackId', etichetta: t('Case pack'), opzioni: 'casePacks' },
  { campo: 'unitOfMeasurementId', etichetta: t('Unità di misura'), opzioni: 'units' },
] as const;

export function Articolo({ id }: { id: number }) {
  const puo = usePuoAprire();
  const [modifiche, setModifiche] = useState<Record<string, string>>({});
  /**
   * Uscendo dalla scheda con modifiche non salvate si chiede: prima si
   * perdevano senza dirlo. Il valore arriva più sotto, quando la scheda è
   * caricata; il blocco sta qui perché un hook non può stare dopo un return.
   */
  const sporca = useRef(false);
  useBlocker({
    shouldBlockFn: () =>
      sporca.current && !window.confirm(t('Ci sono modifiche non salvate: le perdo e vado avanti?')),
    enableBeforeUnload: () => sporca.current,
  });
  const [avviso, setAvviso] = useState<string | null>(null);
  const [anteprimaAperta, setAnteprimaAperta] = useState(false);

  const dati = useArticolo(id);
  const definizioni = useDefinizioneFiltriArticoli();
  const salva = useSalvaArticolo(id);
  const genera = useGeneraGs1(id);
  const anteprima = useAnteprimaGs1(id, anteprimaAperta);

  // con un numero non valido la lettura non parte nemmeno: «in attesa» per
  // sempre. Si aspetta solo una lettura vera, e il resto è «non disponibile»
  if (dati.isLoading) return <div className="cantiere">{t('Un momento…')}</div>;

  if (dati.error || !dati.data) {
    const messaggio =
      dati.error instanceof ErroreApi
        ? dati.error.message
        : t('Non è stato possibile aprire questo articolo.');
    return (
      <>
        <div className="page-head">
          <div>
            <Link className="back-link" to="/items">
              {t('← Torna agli articoli')}
            </Link>
            <h1 className="page-title">{t('Articolo non disponibile')}</h1>
          </div>
        </div>
        <div className="cantiere">
          <p>{messaggio}</p>
        </div>
      </>
    );
  }

  const a = dati.data!;
  const opzioni = definizioni.data?.options ?? {};

  const valore = (campo: keyof DatiArticolo) =>
    modifiche[campo] ?? ((a[campo] as string | number | null) ?? '').toString();
  const cambia = (campo: string) => (v: string) => setModifiche((m) => ({ ...m, [campo]: v }));

  const daSalvare = Object.keys(modifiche).length > 0;
  sporca.current = daSalvare;
  const senzaSku = !a.sku || a.sku === SKU_ASSENTE;

  /**
   * Le tendine mandano numeri, le caselle di testo stringhe, e il vuoto
   * significa «togli». Si smista qui, una volta sola, invece che in venti
   * `onChange`.
   */
  const salvaTutto = () => {
    const corpo: Record<string, unknown> = {};
    for (const [campo, v] of Object.entries(modifiche)) {
      const testo = v.trim();
      if (campo.endsWith('Id')) corpo[campo] = testo ? Number(testo) : null;
      else if (campo === 'sku' || campo === 'description') corpo[campo] = testo;
      else corpo[campo] = testo || null;
    }
    salva.mutate(corpo, {
      onSuccess: (risposta) => {
        setModifiche({});
        setAvviso(risposta.warning ?? null);
      },
    });
  };

  const errore =
    (salva.error instanceof ErroreApi && salva.error) ||
    // i codici doppi li spiega la scheda dei codici, con la scelta
    (genera.error instanceof ErroreApi && genera.error.codice !== 'codiciDoppi' && genera.error) ||
    null;

  const codiciPresenti = CODICI.filter((c) => a[c.chiave]);
  const suggerito = a.skuFromAttributes;

  return (
    <>
      <div className="page-head">
        <div>
          <Link className="back-link" to="/items">
            {t('← Torna agli articoli')}
          </Link>
          <h1 className="page-title">{nomeArticolo(a)}</h1>
          <div className="page-sub">
            {!senzaSku && <span className="num">{a.sku}</span>}
            {a.bcNumber && (
              <>
                {!senzaSku && ' · '}
                <span className="num">{a.bcNumber}</span> in BC
              </>
            )}
            {a.companies.length > 0 &&
              ` · ${t('venduto da {aziende}', { aziende: a.companies.map((c) => c.code).join(` ${t('e')} `) })}`}
            {a.archived && ` · ${t('archiviato')}`}
          </div>
        </div>
        <div className="page-actions">
          {daSalvare && <span className="badge badge-warn">{t('modifiche non salvate')}</span>}
          <button
            className="btn btn-primary"
            onClick={salvaTutto}
            disabled={!daSalvare || salva.isPending}
          >
            {salva.isPending ? 'Salvo…' : 'Salva'}
          </button>
        </div>
      </div>

      {errore && (
        <div className="avviso" role="alert" style={{ margin: '12px 20px 0' }}>
          <span className="avviso-segno">▲</span>
          <span>{errore.message}</span>
        </div>
      )}

      {/* Non è un errore: è una cosa che si deve sapere adesso, perché fra
          tre giorni — quando l'importazione avrà rimesso il campo com'era —
          nessuno collegherebbe le due cose. */}
      {avviso && (
        <div className="avviso" role="status" style={{ margin: '12px 20px 0' }}>
          <span className="avviso-segno">i</span>
          <span>{avviso}</span>
          <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setAvviso(null)}>
            {t('Ho capito')}
          </button>
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Identificazione')}</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
              {t('SKU e descrizione arrivano da Business Central')}
            </span>
          </div>
          <div className="card-body">
            <div className="fields">
              <CampoBc
                id="ar-descrizione"
                etichetta="Descrizione"
                obbligatorio
                larghezzaPiena
                limite={200}
                valore={valore('description')}
                onCambia={cambia('description')}
              />
              <CampoBc
                id="ar-sku"
                etichetta="SKU"
                monospazio
                limite={50}
                valore={senzaSku && !('sku' in modifiche) ? '' : valore('sku')}
                onCambia={cambia('sku')}
              />
              {/* Un suggerimento, non una regola: nel v2 la composizione
                  automatica è spenta da quando gli articoli arrivano da BC, e
                  rimetterla in moto riscriverebbe 558 SKU giusti. Qui si vede
                  e la si usa se serve — serve sugli articoli fatti a mano. */}
              {suggerito && suggerito !== valore('sku') && (
                <div className="field">
                  <label>{t('Dagli attributi verrebbe')}</label>
                  <div className="readonly num" style={{ display: 'flex', gap: 8 }}>
                    <span>{suggerito}</span>
                    <button
                      className="btn btn-ghost"
                      style={{ marginLeft: 'auto', fontSize: 12 }}
                      onClick={() => cambia('sku')(suggerito)}
                    >
                      {t('usa questo')}
                    </button>
                  </div>
                </div>
              )}
              <Dato etichetta="Numero in Business Central" mono>
                {a.bcNumber}
              </Dato>
              <CampoBc
                id="ar-alias"
                etichetta="Alias"
                monospazio
                limite={200}
                valore={valore('alias')}
                onCambia={cambia('alias')}
              />
              <CampoBc
                id="ar-xref"
                etichetta="Riferimento incrociato"
                monospazio
                limite={200}
                valore={valore('crossRef')}
                onCambia={cambia('crossRef')}
              />
              <Dato etichetta="Venduto da" pieno>
                {a.companies.length > 0 &&
                  a.companies.map((c) => (
                    <span key={c.id} className="badge" style={{ marginRight: 6 }}>
                      {c.code}
                    </span>
                  ))}
              </Dato>
              <div className="field span-2">
                <label htmlFor="ar-note">{t('Annotazioni')}</label>
                <textarea
                  className="textarea"
                  id="ar-note"
                  value={valore('notes')}
                  onChange={(e) => cambia('notes')(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Attributi')}</span>
          </div>
          <div className="card-body">
            {/* Sono i sei che nel v2 stanno in sei anagrafiche identiche con
                sei maschere identiche. Qui sono una tabella sola, e in scheda
                si leggono insieme perché insieme descrivono l'articolo. */}
            <div className="fields">
              {TENDINE.map((tendina) => (
                <div className="field" key={tendina.campo}>
                  <label htmlFor={`ar-${tendina.campo}`}>
                    {tendina.etichetta}
                    {tendina.campo === 'lineId' && a.lineBcPrefix && (
                      <span className="dim" style={{ fontWeight: 400 }}>
                        {` — prefisso BC ${a.lineBcPrefix}`}
                      </span>
                    )}
                    {tendina.campo === 'variantId' && a.variantGeneratesGs && (
                      <span className="dim" style={{ fontWeight: 400 }}>
                        {t(' — genera il codice GS1')}
                      </span>
                    )}
                    {tendina.campo === 'casePackId' && a.casePackGsNumber && (
                      <span className="dim" style={{ fontWeight: 400 }}>
                        {` — numero GS1 ${a.casePackGsNumber}`}
                      </span>
                    )}
                  </label>
                  <select
                    className="select"
                    id={`ar-${tendina.campo}`}
                    value={valore(tendina.campo as keyof DatiArticolo)}
                    onChange={(e) => cambia(tendina.campo)(e.target.value)}
                  >
                    <option value="">{t('Non impostato')}</option>
                    {(opzioni[tendina.opzioni] ?? []).map((o) => (
                      <option key={o.id} value={String(o.id)}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Formati e pesi')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              {FORMATI.map((c) => (
                <CampoBc
                  key={c.chiave}
                  id={`ar-${c.chiave}`}
                  etichetta={c.etichetta}
                  monospazio
                  limite={50}
                  valore={valore(c.chiave)}
                  onCambia={cambia(c.chiave)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Codici GS1')}</span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {a.variantGeneratesGs && (
                <>
                  <button
                    className="btn"
                    style={{ fontSize: 12 }}
                    onClick={() => setAnteprimaAperta((v) => !v)}
                  >
                    {anteprimaAperta ? t('Nascondi') : t('Cosa verrebbe?')}
                  </button>
                  <button
                    className="btn"
                    style={{ fontSize: 12 }}
                    onClick={() => genera.mutate({ only14: true })}
                    disabled={genera.isPending}
                    title={t('Rifà i quattro GTIN-14 e lascia stare GTIN-12 e GTIN-13')}
                  >
                    {t('Solo i GTIN-14')}
                  </button>
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      // GTIN-12 e GTIN-13 possono essere codici comprati e scritti
                      // a mano: «Rigenera tutti» li sostituiva senza chiedere
                      if (
                        (a.codeG12 || a.codeG13) &&
                        !window.confirm(
                          t('GTIN-12 e GTIN-13 ci sono già e verranno sostituiti. Se sono codici comprati, usa «Solo i GTIN-14». Rigenero tutti?'),
                        )
                      ) {
                        return;
                      }
                      genera.mutate({});
                    }}
                    disabled={genera.isPending}
                  >
                    {genera.isPending
                      ? 'Genero…'
                      : codiciPresenti.length > 0
                        ? t('Rigenera tutti')
                        : t('Genera')}
                  </button>
                </>
              )}
            </span>
          </div>
          <div className="card-body">
            {/* un codice già di un altro articolo: generando ci si ferma e si
                sceglie; scritto, si continua a vedere */}
            {genera.error instanceof ErroreApi && genera.error.codice === 'codiciDoppi' && (
              <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
                <span className="avviso-segno">▲</span>
                <span>
                  {genera.error.message}{' '}
                  <button
                    className="btn"
                    style={{ fontSize: 12, marginLeft: 6 }}
                    disabled={genera.isPending}
                    onClick={() => genera.mutate({ ...(genera.variables ?? {}), force: true })}
                  >
                    {t('Scrivili lo stesso')}
                  </button>
                </span>
              </div>
            )}
            {(a.gsDuplicates?.length ?? 0) > 0 && (
              <p style={{ marginTop: 0, fontSize: 13, color: 'var(--tomato)' }}>
                {t('Codice uguale a quello di un altro articolo:')}{' '}
                {a.gsDuplicates!.map((d) => `${d.code} → ${d.sku}`).join(' · ')}
              </p>
            )}
            {codiciPresenti.length === 0 ? (
              <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                {a.variantGeneratesGs
                  ? t('La variante prevede il codice, ma non è ancora stato generato.')
                  : t('La variante di questo articolo non prevede la generazione del codice.')}
              </p>
            ) : (
              <div className="fields">
                {CODICI.map((c) => (
                  <Dato key={c.chiave} etichetta={c.etichetta} mono>
                    {a[c.chiave] as string | null}
                  </Dato>
                ))}
              </div>
            )}

            {/* Il v2 chiede «sono già presenti dei valori, sei sicuro di
                volerli sovrascrivere?» dopo aver premuto, cioè nel momento in
                cui non si può più guardare. Qui si guarda prima. */}
            {anteprimaAperta && (
              <div style={{ marginTop: 12, borderTop: '1px solid var(--linea)', paddingTop: 12 }}>
                {anteprima.isPending && <p className="dim" style={{ margin: 0 }}>{t('Un momento…')}</p>}
                {anteprima.data && !anteprima.data.ok && (
                  <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                    {anteprima.data.message}
                  </p>
                )}
                {anteprima.data?.codes && (
                  <>
                    <div className="fields">
                      {CODICI.map((c) => {
                        const nuovo = anteprima.data!.codes![c.chiave];
                        const adesso = a[c.chiave] as string | null;
                        return (
                          <Dato
                            key={c.chiave}
                            etichetta={c.etichetta}
                            mono
                            nota={adesso && adesso !== nuovo ? t(' — cambia') : undefined}
                          >
                            {nuovo}
                          </Dato>
                        );
                      })}
                    </div>
                    {/* Un codice generato non collide mai con un altro
                        generato — ha dentro l'identificativo dell'articolo —
                        ma può scontrarsi con uno comprato, ed è così che nei
                        dati sono nati 27 GTIN-13 su due articoli. */}
                    {anteprima.data.duplicati && anteprima.data.duplicati.length > 0 && (
                      <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: 'var(--tomato)' }}>
                        {t('Questo codice ce l’ha già qualcun altro:')}{' '}
                        {anteprima.data.duplicati
                          .map((d) => `${d.code} → ${d.sku}`)
                          .join(' · ')}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card span-2">
          <div className="card-head">
            <span className="card-title">{t('Imballo')}</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
              {t('dati del CRM: Business Central non li ha')}
            </span>
          </div>
          <div className="card-body">
            <div className="fields">
              {IMBALLO.map((c) => (
                <CampoBc
                  key={c.chiave}
                  id={`ar-${c.chiave}`}
                  etichetta={c.etichetta}
                  monospazio
                  limite={c.limite}
                  valore={valore(c.chiave)}
                  onCambia={cambia(c.chiave)}
                />
              ))}
            </div>
          </div>
        </div>

        {a.usage && (
          <div className="card span-2">
            <div className="card-head">
              <span className="card-title">{t('Quanto è stato ordinato')}</span>
              {a.usage.orders > 0 && !senzaSku && puo('sales-orders') && (
                <Link
                  className="cell-link"
                  style={{ marginLeft: 'auto', fontSize: 12 }}
                  to="/sales-orders"
                  search={{ articleId: a.id } as never}
                >
                  {t('Vedi gli ordini →')}
                </Link>
              )}
            </div>
            <div className="card-body">
              {/* La domanda che ci si fa guardando un articolo. Nel v2 bisogna
                  uscire e cercarlo fra 55.834 righe d'ordine. */}
              <div className="fields">
                <Dato etichetta="Ordini che lo contengono" mono>
                  {a.usage.orders > 0 ? a.usage.orders.toLocaleString('it-IT') : null}
                </Dato>
                <Dato etichetta="Quantità totale" mono>
                  {a.usage.quantity > 0 ? quantita(a.usage.quantity) : null}
                </Dato>
                <Dato etichetta="Ultimo ordine">
                  {a.usage.lastOrderDate ? data(a.usage.lastOrderDate) : null}
                </Dato>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
